import { StyleSheet, View } from "react-native";
import React, { useState, useCallback, useMemo } from "react";
import { useFocusEffect, useRouter } from "expo-router";
import { entitySync } from "@/services/sync/entity-sync";
import { useAuth } from "@components/auth/useAuth";

import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedList, type ISortOption, type IGroupOption } from "@/components/ThemedList";
import { ThemedActionHoverPanel, type IBatchAction } from "@/components/ThemedActionHoverPanel";
import { ShareRecipientSelector } from "@/components/sharing/ShareRecipientSelector";

import { useFirebase } from "@components/firebase/useFirebase";
import { getMyAnchors, deleteAnchor } from "@/services/firebase/anchor-functions";
import { confirmEntityDeletion } from "@/services/firebase/delete-confirmation";
import { share, unshare, getSharesForSource } from "@/services/firebase/share-functions";
import { getMyContacts } from "@/services/firebase/user-functions";
import { getMyGroups } from "@/services/firebase/group-functions";
import { getMyPlaces } from "@/services/firebase/place-functions";
import { AnchorModel, ShareModel, Contact, GroupModel, PlaceModel } from "common247";
import { AnchorCard } from "@/components/anchors/AnchorCard";
import { CollapsibleFilterSection } from "@/components/filter";
import { useFilterPersistence } from "@/hooks/useFilterPersistence";
import { filterAnchors, computeFilteredCounts } from "@/services/unified-filter-service";

const ANCHOR_SORT_OPTIONS: ISortOption<AnchorModel>[] = [
  { key: "name", label: "Anchor Name (A-Z)", getValue: (a) => a.name || "" },
  { key: "type", label: "Type (A-Z)", getValue: (a) => a.type || "" },
  { key: "address", label: "Address", getValue: (a) => a.address || "" },
  { key: "createdAt", label: "Date Created", getValue: (a) => -(a.createdAt || 0) },
];

const ANCHOR_GROUP_OPTIONS: IGroupOption<AnchorModel>[] = [
  {
    key: "role",
    label: "Role",
    getGroupValue: (a) =>
      a.userRole === "owner"
        ? "My Anchors (Owner)"
        : `Shared with me (${a.userRole || "Viewer"})`,
  },
  {
    key: "type",
    label: "Type",
    getGroupValue: (a) =>
      a.type ? a.type.charAt(0).toUpperCase() + a.type.slice(1) : "Other",
  },
  {
    key: "tag",
    label: "Primary Tag",
    getGroupValue: (a) => {
      const tags = Object.values(a.tags || {});
      return tags.length > 0 ? tags[0].value : "Untagged";
    },
  },
];

export default function MyAnchorsScreen(): React.JSX.Element {
  const services = useFirebase();
  const { user } = useAuth();
  const router = useRouter();

  const [anchors, setAnchors] = useState<AnchorModel[]>([]);
  const [loading, setLoading] = useState(true);

  // Unified filter persistence
  const { filter, setFilter, resetFilter } = useFilterPersistence("anchors");

  // Selection state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  // Sharing state
  const [sharingAnchor, setSharingAnchor] = useState<AnchorModel | null>(null);
  const [shares, setShares] = useState<ShareModel[]>([]);
  const [sharesByEntityId, setSharesByEntityId] = useState<Record<string, ShareModel[]>>({});
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [groups, setGroups] = useState<GroupModel[]>([]);
  const [places, setPlaces] = useState<PlaceModel[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);

  const effectiveAnchors = useMemo(() => {
    return filterAnchors(anchors, filter);
  }, [anchors, filter]);

  const filteredCounts = useMemo(() => {
    return computeFilteredCounts({ anchors }, filter, "anchors");
  }, [anchors, filter]);

  const isSelectionActive = selectionMode || selectedIds.length > 0;

  const fetchAllData = useCallback(async () => {
    if (!services) return;
    setLoading(true);
    try {
      const [response, contactsRes, groupsRes, placesRes] = await Promise.all([
        getMyAnchors(services, { filter }),
        getMyContacts(services, { includeInactive: false }).catch(() => ({ status: false, contacts: [] as Contact[] })),
        getMyGroups(services, {}).catch(() => ({ status: false, groups: [] as GroupModel[] })),
        getMyPlaces(services, {}).catch(() => ({ status: false, places: [] as PlaceModel[] })),
      ]);
      if (response.status) {
        setAnchors(response.anchors || []);
      }
      if (contactsRes.status && contactsRes.contacts) {
        setContacts(contactsRes.contacts);
      }
      if (groupsRes.status && groupsRes.groups) {
        setGroups(groupsRes.groups);
      }
      if (placesRes.status && placesRes.places) {
        setPlaces(placesRes.places);
      }
    } catch (err) {
      console.error("[MyAnchorsScreen] Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [services, filter]);

  useFocusEffect(
    useCallback(() => {
      fetchAllData();
    }, [fetchAllData])
  );

  // Entity sync listener
  useFocusEffect(
    useCallback(() => {
      const unsub = entitySync.subscribe((event) => {
        if (event.type === "anchor") {
          fetchAllData();
        }
      });
      return () => unsub();
    }, [fetchAllData])
  );

  const fetchSharesForAnchor = async (anchorId: string) => {
    if (!services || !anchorId) return;
    setLoadingShares(true);
    try {
      const res = await getSharesForSource(services, {
        sourceId: anchorId,
        sourceType: "anchor",
      });
      if (res.status && res.shares) {
        setShares(res.shares);
        setSharesByEntityId((prev) => ({ ...prev, [anchorId]: res.shares || [] }));
      }
    } catch (err) {
      console.error("[MyAnchorsScreen] Error fetching shares:", err);
    } finally {
      setLoadingShares(false);
    }
  };

  const handleDeleteAnchor = async (anchor: AnchorModel) => {
    if (!services || !anchor.id) return;

    confirmEntityDeletion(
      services,
      "anchor",
      anchor.id,
      async () => {
        try {
          const res = await deleteAnchor(services, { anchorId: anchor.id });
          if (res.status) {
            setAnchors((prev) => prev.filter((a) => a.id !== anchor.id));
          } else {
            CustomAlert.alert("Error", res.error || "Failed to delete anchor.");
          }
        } catch (err: any) {
          CustomAlert.alert("Error", err.message || "Deletion failed.");
        }
      },
      anchor.name
    );
  };

  const handleShareTarget = async (
    targetId: string,
    targetType: "contact" | "group",
    role: "viewer" | "editor"
  ) => {
    if (!services || !sharingAnchor?.id) return;
    try {
      const res = await share(services, {
        sourceId: sharingAnchor.id,
        sourceType: "anchor",
        targetId,
        targetType,
        role,
      });
      if (res.status) {
        await fetchSharesForAnchor(sharingAnchor.id);
      } else {
        CustomAlert.alert("Sharing Error", res.error || "Failed to share anchor.");
      }
    } catch (err: any) {
      CustomAlert.alert("Sharing Error", err.message || "Failed to share anchor.");
    }
  };

  const handleUnshare = async (shareId: string) => {
    if (!services || !sharingAnchor?.id) return;
    try {
      const res = await unshare(services, { shareId });
      if (res.status) {
        await fetchSharesForAnchor(sharingAnchor.id);
      } else {
        CustomAlert.alert("Error", res.error || "Failed to revoke share.");
      }
    } catch (err: any) {
      CustomAlert.alert("Error", err.message || "Failed to revoke share.");
    }
  };

  // Batch actions
  const batchActions: IBatchAction[] = [
    {
      id: "share",
      label: "Share",
      icon: "share-social-outline",
      onPress: () => {
        if (selectedIds.length === 0) return;
        const first = anchors.find((a) => a.id === selectedIds[0]);
        if (first) {
          setSharingAnchor(first);
          fetchSharesForAnchor(first.id);
          setIsShareModalOpen(true);
        }
      },
    },
    {
      id: "delete",
      label: "Delete",
      icon: "trash-outline",
      destructive: true,
      onPress: () => {
        if (selectedIds.length === 0) return;
        CustomAlert.alert(
          "Delete Selected Anchors",
          `Are you sure you want to delete ${selectedIds.length} selected anchor(s)?`,
          [
            { text: "Cancel", style: "cancel" },
            {
              text: "Delete",
              style: "destructive",
              onPress: async () => {
                for (const id of selectedIds) {
                  await deleteAnchor(services, { anchorId: id }).catch(() => {});
                }
                setSelectedIds([]);
                setSelectionMode(false);
                fetchAllData();
              },
            },
          ]
        );
      },
    },
  ];

  const handleClearSelection = () => {
    setSelectedIds([]);
    setSelectionMode(false);
  };

  return (
    <ThemedScreen title="Anchors" loading={loading} scrollable={false}>
      <View style={styles.container}>
        {/* Unified Filter Section */}
        <CollapsibleFilterSection
          screen="anchors"
          filter={filter}
          onFilterChange={setFilter}
          counts={filteredCounts}
          onReset={resetFilter}
          allAnchors={anchors}
          allContacts={contacts as any}
          allGroups={groups}
          allPlaces={places}
        />

        {/* List of Anchors */}
        <ThemedList<AnchorModel>
          data={effectiveAnchors}
          loading={loading}
          enableUrlSync={true}
          selectionMode={isSelectionActive}
          selectedIds={selectedIds}
          onSelectedIdsChange={setSelectedIds}
          sortOptions={ANCHOR_SORT_OPTIONS}
          defaultSortKey="name"
          defaultSortDirection="asc"
          groupOptions={ANCHOR_GROUP_OPTIONS}
          persistenceKey="my-anchors"
          entityType="anchor"
          onRefresh={fetchAllData}
          refreshing={loading}
          addButton={{
            title: isSelectionActive ? "Back" : "+ Add Anchor",
            onPress: () =>
              isSelectionActive
                ? handleClearSelection()
                : router.push("/(tabs)/my-anchors/new"),
            variant: isSelectionActive ? "secondary" : "primary",
            testID: "new-anchor-button",
          }}
          renderItem={(item, _index, isHovered, viewMode) => {
            const isSelected = selectedIds.includes(item.id);
            return (
              <AnchorCard
                anchor={item}
                viewMode={viewMode}
                isHovered={isHovered}
                selectionMode={isSelectionActive}
                isSelected={isSelected}
                onPress={() => {
                  if (isSelectionActive) {
                    setSelectedIds((prev) =>
                      prev.includes(item.id)
                        ? prev.filter((id) => id !== item.id)
                        : [...prev, item.id]
                    );
                  } else {
                    router.push(`/(tabs)/my-anchors/${item.id}`);
                  }
                }}
                onEditPress={() => router.push(`/(tabs)/my-anchors/${item.id}`)}
                onDelete={() => handleDeleteAnchor(item)}
                onShare={() => {
                  setSharingAnchor(item);
                  fetchSharesForAnchor(item.id);
                  setIsShareModalOpen(true);
                }}
                shares={sharesByEntityId[item.id]}
                availableContacts={contacts as any}
                availableGroups={groups}
                availablePlaces={places}
                isLoadingShares={loadingShares && sharingAnchor?.id === item.id}
                onShareTarget={handleShareTarget}
                onUnshare={handleUnshare}
                currentUserId={user?.uid}
              />
            );
          }}
          keyExtractor={(item) => item.id}
          emptyText="No anchors found."
        />

        {/* Batch Selection Action Hover Panel */}
        {isSelectionActive && (
          <ThemedActionHoverPanel
            visible={isSelectionActive}
            selectedCount={selectedIds.length}
            actions={batchActions}
            onClearSelection={() => {
              setSelectedIds([]);
              setSelectionMode(false);
            }}
          />
        )}

        {/* Share Recipient Selector Modal */}
        {isShareModalOpen && sharingAnchor && (
          <ShareRecipientSelector
            visible={isShareModalOpen}
            onClose={() => {
              setIsShareModalOpen(false);
              setSharingAnchor(null);
            }}
            sourceId={sharingAnchor.id}
            sourceType="anchor"
            shares={shares}
            availableContacts={contacts}
            availableGroups={groups}
            isLoading={loadingShares}
            onShare={handleShareTarget}
            onUnshare={handleUnshare}
          />
        )}
      </View>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
